import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const GoogleButton = () => {
  const handle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      onClick={handle}
      data-testid="google-login-btn"
      className="w-full flex items-center justify-center gap-3 border border-white/15 hover:border-white/40 h-12 rounded-sm text-sm text-white/80 transition-colors"
    >
      <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
      Continue with Google
    </button>
  );
};

const Shell = ({ title, children }) => (
  <div className="min-h-screen bg-[#050505] grain flex">
    <div className="hidden lg:block lg:w-1/2 relative overflow-hidden border-r border-white/10">
      <img src="https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover grayscale opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
      <div className="relative z-10 h-full flex flex-col justify-between p-12">
        <Link to="/" className="font-serif text-3xl text-white">Ai<span className="text-cyan-400">.</span>Sho</Link>
        <div>
          <p className="font-serif text-4xl text-white leading-tight max-w-md">Enterprise efficiency, engineered.</p>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-400 mt-6">Client Portal Access</p>
        </div>
      </div>
    </div>
    <div className="flex-1 flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-sm">
        <Link to="/" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-cyan-400 transition-colors mb-10">
          <ArrowLeft size={13} /> Back to site
        </Link>
        <h1 className="font-serif text-4xl text-white mb-2">{title}</h1>
        {children}
      </motion.div>
    </div>
  </div>
);

export function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data);
      toast.success("Welcome back.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <Shell title="Client Portal">
      <p className="text-sm text-white/50 mb-8">Sign in to access your dashboard.</p>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Email</Label>
          <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" className="bg-[#0C0C0C] border-white/15 h-12 rounded-sm focus-visible:ring-cyan-400" />
        </div>
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Password</Label>
          <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password" className="bg-[#0C0C0C] border-white/15 h-12 rounded-sm focus-visible:ring-cyan-400" />
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-cyan-400 hover:bg-white text-black rounded-sm h-12 font-mono text-[11px] uppercase tracking-[0.2em]">
          {loading ? "Signing in…" : "Sign In"} <ArrowUpRight size={15} className="ml-1" />
        </Button>
      </form>
      <div className="flex items-center gap-4 my-6">
        <span className="h-px flex-1 bg-white/10" /><span className="font-mono text-[10px] text-white/30">OR</span><span className="h-px flex-1 bg-white/10" />
      </div>
      <GoogleButton />
      <p className="mt-8 text-sm text-white/50 text-center">
        No account? <Link to="/register" className="text-cyan-400 hover:underline" data-testid="to-register">Create one</Link>
      </p>
    </Shell>
  );
}

export function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Account created.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <Shell title="Create Account">
      <p className="text-sm text-white/50 mb-8">Join the AiSho client portal.</p>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Full Name</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" className="bg-[#0C0C0C] border-white/15 h-12 rounded-sm focus-visible:ring-cyan-400" />
        </div>
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Email</Label>
          <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" className="bg-[#0C0C0C] border-white/15 h-12 rounded-sm focus-visible:ring-cyan-400" />
        </div>
        <div className="space-y-2">
          <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Password</Label>
          <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" className="bg-[#0C0C0C] border-white/15 h-12 rounded-sm focus-visible:ring-cyan-400" />
        </div>
        <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-cyan-400 hover:bg-white text-black rounded-sm h-12 font-mono text-[11px] uppercase tracking-[0.2em]">
          {loading ? "Creating…" : "Create Account"} <ArrowUpRight size={15} className="ml-1" />
        </Button>
      </form>
      <div className="flex items-center gap-4 my-6">
        <span className="h-px flex-1 bg-white/10" /><span className="font-mono text-[10px] text-white/30">OR</span><span className="h-px flex-1 bg-white/10" />
      </div>
      <GoogleButton />
      <p className="mt-8 text-sm text-white/50 text-center">
        Have an account? <Link to="/login" className="text-cyan-400 hover:underline" data-testid="to-login">Sign in</Link>
      </p>
    </Shell>
  );
}

export default Login;
